import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  getOrganizationRole,
  OrganizationRole,
} from '@/components/organizations/details/utils'
import type { AuthUser } from '@/hooks/useAuth'
import { usePortfolios } from './usePortfolios'

interface UsePortfolioAdminWithoutPortfolioProps {
  organizationId: string | null
  user: AuthUser | null
}

export const usePortfolioAdminWithoutPortfolio = ({
  organizationId,
  user,
}: UsePortfolioAdminWithoutPortfolioProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { error, portfolios, isPending } = usePortfolios({
    organizationId,
  })

  const publicMetadata = user?.publicMetadata as
    | Record<string, unknown>
    | undefined

  const userRole = getOrganizationRole(publicMetadata, organizationId ?? '')

  const isPortfoliosReady = !isPending && !error
  const isPortfolioManager = userRole === OrganizationRole.PortfolioManager
  const hasNoPortfolios = portfolios.length === 0
  const isOutsidePortfolioOnboarding = !pathname.startsWith(
    '/onboarding/portfolio'
  )

  const shouldBlockPortfolioAdminWithoutPortfolio =
    isPortfoliosReady &&
    isPortfolioManager &&
    hasNoPortfolios &&
    isOutsidePortfolioOnboarding

  useEffect(() => {
    if (!shouldBlockPortfolioAdminWithoutPortfolio) {
      return
    }

    navigate('/onboarding/portfolio', { replace: true })
  }, [navigate, shouldBlockPortfolioAdminWithoutPortfolio])

  return {
    shouldBlockPortfolioAdminWithoutPortfolio,
    hasPortfolios: !isPortfoliosReady || !hasNoPortfolios,
  }
}
